import { StakeholderData } from '../types/StakeholderData.ts';
import { ChartContext } from './ChartContext';
import { CircleFactory } from './CircleFactory';
import { FilledCircle } from './FilledCircle';
import { Label } from './Label';
import { LabelLine } from './LabelLine';
import { RelativePosition } from './RelativePosition';
import { RelativeRadius } from './RelativeRadius';

const COLOR = {
    RED: '#CC5B64',
    LIGHT_RED: '#FFB3B3',
    LIGHT_BLUE: '#ADD8E6',
    GREY: '#CCCCCC'
};

export class Stakeholder {
    private filledCircle: FilledCircle;
    private label!: Label;
    private labelLine!: LabelLine;

    constructor(
        chartContext: ChartContext,
        data: StakeholderData,
        angle?: number
    ) {
        const exposition = data.dependency * data.penetration;
        const fiability = data.maturity * data.trust;
        const threatLevel = fiability !== 0 ? exposition / fiability : 0;

        let radiusValue = 0.02;
        if (exposition > 9) {
            radiusValue = 0.08;
        } else if (exposition > 6) {
            radiusValue = 0.06;
        } else if (exposition > 3) {
            radiusValue = 0.04;
        }

        // Set color based on fiability
        let color = COLOR.GREY;
        if (fiability < 4) {
            color = COLOR.RED;
        } else if (fiability <= 5) {
            color = COLOR.LIGHT_RED;
        } else if (fiability <= 7) {
            color = COLOR.LIGHT_BLUE;
        }

        const pointPos = new RelativePosition(
            chartContext.maxRadius,
            chartContext.centerPosition,
            threatLevel,
            -(angle ?? Math.PI / 2)); // negative angle because y axis is pointing down in HTML canvas

        this.filledCircle = CircleFactory.createFilled(
            chartContext.ctx,
            pointPos,
            new RelativeRadius(chartContext.maxRadius, radiusValue),
            color
        );

        if(angle != null) {
            if(angle <= Math.PI / 2) {
                this.label = new Label(
                    chartContext.ctx,
                    data.text,
                    chartContext.rightEdgeX() + chartContext.margin,
                    pointPos.position().y,
                    '#FFF',
                    chartContext.fontSize,
                    'left',
                    'middle',
                    chartContext.canvas.width / 3);
                this.labelLine = new LabelLine(
                    chartContext.ctx, 
                    pointPos.position(), 
                    chartContext.rightEdgeX()  + chartContext.margin, 
                    pointPos.position().y);
            } else {
                this.label = new Label(
                    chartContext.ctx,
                    data.text,
                    chartContext.leftEdgeY() - chartContext.margin,
                    pointPos.position().y,
                    '#FFF',
                    chartContext.fontSize,
                    'right',
                    'middle',
                    chartContext.canvas.width / 3
                );
                this.labelLine = new LabelLine(
                    chartContext.ctx, 
                    pointPos.position(), 
                    chartContext.leftEdgeY() - chartContext.margin, 
                    pointPos.position().y);
            }
        }
    }

    draw(): void {
        this.filledCircle.draw();
        this.labelLine.draw();
        this.label.draw();
    }
}